import { useId } from 'react';

type Variant = 'wordmark' | 'monogram';
type Size = 'sm' | 'md' | 'lg';
type Scheme = 'light' | 'dark';

export interface LogoProps {
  variant?: Variant;
  size?: Size;
  scheme?: Scheme;
  className?: string;
}

const HEIGHTS: Record<Size, number> = { sm: 24, md: 32, lg: 48 };

// HSL brand token values matching index.css design tokens
const BRAND = {
  gold:      'hsl(42 88% 55%)',
  goldGlow:  'hsl(42 95% 65%)',
  darkMark:  'hsl(25 25% 15%)',   // --primary-foreground (on gold bg)
  lightText: 'hsl(35 25% 92%)',   // --sidebar-foreground
  darkText:  'hsl(25 25% 15%)',   // --foreground
  mutedLight:'hsl(25 15% 45%)',   // --muted-foreground
  mutedDark: 'hsl(32 15% 65%)',   // dark-mode muted-foreground
} as const;

// Wordmark coordinate space: 190 × 40 units
// Layout: [0–40] monogram box | [52–] "For Visibility" @ 18px Playfair
const WM_VIEW_W = 200;
const VIEW_H    = 40;

// TODO: Replace Logo.tsx with For Visibility brand asset (forvisibility.com)
export function Logo({
  variant = 'wordmark',
  size    = 'md',
  scheme  = 'light',
  className,
}: LogoProps) {
  const uid    = useId();
  const gradId = `logo-grad-${uid.replace(/:/g, '')}`;

  const h           = HEIGHTS[size];
  const textColor   = scheme === 'light' ? BRAND.darkText   : BRAND.lightText;
  const mutedColor  = scheme === 'light' ? BRAND.mutedLight : BRAND.mutedDark;

  const sharedDefs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor={BRAND.gold} />
        <stop offset="100%" stopColor={BRAND.goldGlow} />
      </linearGradient>
    </defs>
  );

  if (variant === 'monogram') {
    return (
      <svg
        width={h}
        height={h}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="For Visibility"
        className={className}
      >
        {sharedDefs}
        <rect width="40" height="40" rx="8" fill={`url(#${gradId})`} />
        <text
          x="20"
          y="21"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="'Playfair Display', Georgia, serif"
          fontWeight="700"
          fontSize="22"
          fill={BRAND.darkMark}
        >
          F
        </text>
      </svg>
    );
  }

  // wordmark: monogram box + "Flipper" (Playfair Display) + "OS" (Inter)
  const displayW = Math.round(h * (WM_VIEW_W / VIEW_H));

  return (
    <svg
      width={displayW}
      height={h}
      viewBox={`0 0 ${WM_VIEW_W} ${VIEW_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="For Visibility"
      className={className}
    >
      {sharedDefs}

      {/* Monogram box */}
      <rect width="40" height="40" rx="8" fill={`url(#${gradId})`} />
      <text
        x="20"
        y="21"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="'Playfair Display', Georgia, serif"
        fontWeight="700"
        fontSize="22"
        fill={BRAND.darkMark}
      >
        F
      </text>

      {/* "For Visibility" in Playfair Display */}
      <text
        x="52"
        y="21"
        dominantBaseline="central"
        fontFamily="'Playfair Display', Georgia, serif"
        fontWeight="600"
        fontSize="18"
        fill={textColor}
      >
        For Visibility
      </text>

      {/* tagline */}
      <text
        x="52"
        y="38"
        dominantBaseline="central"
        fontFamily="'Inter', system-ui, sans-serif"
        fontWeight="400"
        fontSize="9"
        fill={mutedColor}
      >
      </text>
    </svg>
  );
}
